import { useEffect, useMemo, useState } from 'react';
import type { LeadCloudFileType } from '@econtractor/types';
import { useTranslation } from 'react-i18next';
import { useToast } from 'context/ToastContext';
import * as apiLead from 'api/apiLeads';
import * as apiProposal from 'api/apiProposal';
import vconst from 'utils/helpers/structs/vconst';
import { uploadFilesWithOverallProgress } from 'utils/helpers/featchFiles/uploadFileWithProgress';
import { escapeHtml } from 'utils/helpers/functions/escapeHtml';
import { buildDocumentMailContent } from 'utils/helpers/email/buildMailContent';
import { createPdfBlob } from 'utils/helpers/pdf/pdfMake';
import { buildProposalPdfDefinition } from '../../share/model/helper/proposalPdfDefinition';
import { fileToPdfCompatibleDataUrl } from '../../share/model/helper/fileToPdfCompatibleDataUrl';
import useTemplateValueKeys from '../../share/model/useTemplateValueKeys';
import {
    getProposalTotalAmount,
    getProposalUseUsd,
} from '../../share/model/helper/proposalPriceUtils';
import type { ProposalEditFormValues } from '../types';

interface UseProposalGenerateParams {
    leadId?: number | string;
    leadProposalId?: number | string;
    leadEmail?: string;
    /** Статус події при формуванні. Для погодженої заявки — leadAccept (без зміни статусу). */
    eventStatusId?: number;
    formData: ProposalEditFormValues;
    rate: number;
    /** Lead/client record for template key substitution. */
    objectData?: unknown;
    onGenerated?: () => Promise<void> | void;
}

const useProposalGenerate = ({
    leadId,
    leadProposalId,
    leadEmail = '',
    eventStatusId = vconst.leadOffer,
    formData,
    rate,
    objectData,
    onGenerated,
}: UseProposalGenerateParams) => {
    const { t } = useTranslation(['Proposal', 'common']);
    const templateValueKeys = useTemplateValueKeys();
    const { showToast } = useToast();
    const sourceEmail = String(leadEmail || '').trim();
    const [sendToClient, setSendToClient] = useState(false);
    const [clientEmail, setClientEmail] = useState(sourceEmail);
    const [generating, setGenerating] = useState(false);

    const canGenerate = useMemo(() => {
        const targetLeadId = Number(leadId || 0);
        const targetLeadProposalId = Number(leadProposalId || 0);
        if (!targetLeadId || !targetLeadProposalId) return false;
        if (sendToClient && !String(clientEmail || '').trim()) return false;
        return true;
    }, [leadId, leadProposalId, sendToClient, clientEmail]);

    useEffect(() => {
        if (!sourceEmail) return;
        setClientEmail((prev) => (String(prev || '').trim() ? prev : sourceEmail));
    }, [sourceEmail]);

    const handleSendToClientChange = (checked: boolean) => {
        setSendToClient(checked);
        if (checked && sourceEmail) {
            setClientEmail(sourceEmail);
        }
    };

    const getPdfBlobFromDefinition = async (docDefinition: unknown): Promise<Blob> => {
        try {
            return await createPdfBlob(docDefinition);
        } catch {
            throw new Error('proposal_pdf_blob_empty');
        }
    };

    const buildPdfBlob = async (): Promise<Blob> => {
        const hasLogo = Boolean(String(formData.proposalLogo || '').trim());
        const hasBackground = Boolean(String(formData.proposalBackground || '').trim());
        const docDefinition = await buildProposalPdfDefinition({
            t: (key: string, options?: Record<string, unknown>) => t(key, options || {}),
            templateValueKeys,
            formData,
            rate,
            generatedAt: Date.now(),
            logoDataUrl: hasLogo ? await fileToPdfCompatibleDataUrl(String(formData.proposalLogo || '')) : '',
            backgroundDataUrl: hasBackground
                ? await fileToPdfCompatibleDataUrl(String(formData.proposalBackground || ''))
                : '',
            resolveImageDataUrl: fileToPdfCompatibleDataUrl,
            objectData,
            includeCoverPage: Boolean(formData.proposalTitlePage),
        });
        return await getPdfBlobFromDefinition(docDefinition);
    };

    const uploadPdfToCloudflare = async (leadIdValue: number, file: File): Promise<LeadCloudFileType | null> => {
        const uploadConfigs = await apiLead.getCloudflareUploadUrls(leadIdValue, [
            {
                fileName: file.name,
                contentType: file.type || 'application/pdf',
                fileSize: file.size,
            },
        ]);
        if (!uploadConfigs?.length) return null;

        const uploaded = await uploadFilesWithOverallProgress([file], uploadConfigs);
        const uploadedFile = uploaded?.[0];
        if (!uploadedFile?.success) return null;
        return uploadedFile.config;
    };

    const handleGenerate = async (): Promise<boolean> => {
        if (generating) return false;
        const targetLeadId = Number(leadId || 0);
        const targetLeadProposalId = Number(leadProposalId || 0);
        if (!targetLeadId || !targetLeadProposalId) {
            showToast(t('common:messError'), 'danger');
            return false;
        }
        if (sendToClient && !String(clientEmail || '').trim()) {
            showToast(t('edit.generate.clientEmailRequired'), 'warning');
            return false;
        }

        setGenerating(true);
        try {
            const pdfBlob = await buildPdfBlob();
            const baseName = String(formData.proposalName || '')
                .trim()
                .replace(/[\\/:*?"<>|]+/g, ' ')
                .replace(/\s+/g, ' ')
                .trim();
            const fileName = `${baseName || t('edit.generate.fileNameFallback')}.pdf`;
            const pdfFile = new File([pdfBlob], fileName, { type: 'application/pdf' });
            const cloudFile = await uploadPdfToCloudflare(targetLeadId, pdfFile);
            if (!cloudFile) {
                showToast(t('common:messError'), 'danger');
                return false;
            }

            const email = String(clientEmail || '').trim();
            const comment = String(formData.proposalComment || '').trim().slice(0, 500);
            // Одна подія: формування КП + одразу активна (без окремої «Зміна активної»).
            const eventInfoFull = [
                t('edit.generate.eventInfoGeneratedActive'),
                sendToClient && email ? t('edit.generate.eventInfoWithEmail', { email }) : '',
                comment ? escapeHtml(comment) : '',
            ]
                .filter(Boolean)
                .join('<br><br>');

            const eventCreated = await apiLead.addEvent(targetLeadId, {
                statusID: eventStatusId || vconst.leadOffer,
                eventInfo: eventInfoFull,
                eventAttachCloud: [cloudFile],
            });
            if (!eventCreated) {
                showToast(t('common:messError'), 'danger');
                return false;
            }

            const logCreated = await apiProposal.createLeadProposalGeneration(targetLeadId, {
                leadProposalId: targetLeadProposalId,
                proposalName: formData.proposalName,
                proposalNumber: String(formData.proposalNumber || '').trim() || null,
                proposalDt: formData.proposalDt && formData.proposalDt > 0 ? formData.proposalDt : null,
                file: cloudFile,
                sentToEmail: sendToClient && Boolean(email),
                sentEmail: sendToClient ? email : '',
                generationComment: comment,
                proposalAmount: getProposalTotalAmount(formData, rate),
                proposalUsd: getProposalUseUsd(formData.pricesInUsd, rate),
                proposalVat: Boolean(formData.proposalVat),
                products: formData.products || [],
                additionalItems: formData.additionalItems || [],
                pricesInUsd: Boolean(formData.pricesInUsd),
            });
            if (!logCreated) {
                showToast(t('common:messError'), 'warning');
            }

            if (sendToClient && email) {
                const mailContent = await buildDocumentMailContent({
                    type: 'proposal',
                    documentName: formData.proposalName || cloudFile.fileName || 'proposal',
                    fileUrl: cloudFile.publicUrl,
                });
                if (mailContent) {
                    await apiProposal.sendLeadProposalEmail(targetLeadId, {
                        email,
                        file: cloudFile,
                        proposalName: formData.proposalName,
                        mailContent,
                    });
                }
            }

            showToast(t('edit.generate.successCreated'), 'success');
            if (onGenerated) await onGenerated();
            return true;
        } catch (e) {
            console.error(e);
            showToast(t('common:messError'), 'danger');
            return false;
        } finally {
            setGenerating(false);
        }
    };

    return {
        sendToClient,
        setSendToClient: handleSendToClientChange,
        clientEmail,
        setClientEmail,
        generating,
        canGenerate,
        handleGenerate,
    };
};

export default useProposalGenerate;
